import { CancelButton, SaveButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { InfiniteUserSelect } from "@components/InfiniteUserSelect"
import {
  Stack,
  Textarea,
  Switch,
  Group,
  Tooltip,
  ActionIcon,
  Text,
} from "@mantine/core"
import { DatePickerPreset, DateTimePicker } from "@mantine/dates"
import {
  IconChevronCompactUp,
  IconChevronCompactDown,
} from "@tabler/icons-react"
import { PersonNameWithUserNameFallback } from "../../chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { UseFormReturnType } from "@mantine/form"
import {
  CreateReminderRequest,
  EditReminderRequest,
  ReminderDto,
} from "@/orval/gen"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { debugLog } from "@/utils/debugLog"
import { OwnerGroupSingleSelectForReminders } from "../owner-group-single-select"

type ReminderFormValues = CreateReminderRequest | EditReminderRequest

interface Props {
  form: UseFormReturnType<
    ReminderFormValues,
    (values: ReminderFormValues) => ReminderFormValues
  >
  reminder?: ReminderDto
  handleSubmit: (values: ReminderFormValues) => Promise<void> | void
  onCancel?: () => void
  isSubmitting?: boolean
  onDelete?: () => void
  isDeleting?: boolean
}

export function CreateOrEditReminderForm({
  form,
  reminder,
  handleSubmit,
  onCancel,
  isSubmitting = false,
  onDelete,
  isDeleting = false,
}: Props) {
  const { t } = useTranslation("app_server")
  const [expanded, setExpanded] = React.useState(false)
  const [withoutTime, setWithoutTime] = React.useState(
    reminder ? !reminder.time : false
  )
  const submitInProgressRef = React.useRef(false)
  const [isSubmittingLocally, setIsSubmittingLocally] = React.useState(false)
  const submitting = isSubmitting || isSubmittingLocally

  React.useEffect(() => {
    if (reminder) {
      setWithoutTime(!reminder.time)
    }
  }, [reminder])

  const [dateTimePickerResetKey, setDateTimePickerResetKey] = React.useState(0)
  const now = React.useMemo(() => dayjs(), [])
  const presets: DatePickerPreset<"default">[] = [
    {
      label: `${t("In")} 15 ${t("Minutes")}`,
      value: now.add(15, "minute").toISOString(),
    },
    {
      label: `${t("In")} 30 ${t("Minutes")}`,
      value: now.add(30, "minute").toISOString(),
    },
    {
      label: `${t("In")} 1 ${t("Hour")}`,
      value: now.add(1, "hour").toISOString(),
    },
    {
      label: `${t("In")} 2 ${t("Hours")}`,
      value: now.add(2, "hour").toISOString(),
    },
    {
      label: `${t("In")} 24 ${t("Hours")}`,
      value: now.add(24, "hour").toISOString(),
    },
  ]
  const presetTimestamps = React.useMemo(
    () => new Set(presets.map((preset) => dayjs(preset.value).valueOf())),
    [presets]
  )

  const handleFormSubmit = form.onSubmit(
    async (values) => {
      try {
        await handleSubmit({
          ...values,
          time:
            values.time && !withoutTime
              ? dayjs(values.time).utc(true).toISOString()
              : undefined,
        })
      } finally {
        submitInProgressRef.current = false
        setIsSubmittingLocally(false)
      }
    },
    () => {
      submitInProgressRef.current = false
      setIsSubmittingLocally(false)
    }
  )

  return (
    <form
      onSubmit={(event) => {
        if (submitInProgressRef.current || submitting) {
          event.preventDefault()
          return
        }

        submitInProgressRef.current = true
        setIsSubmittingLocally(true)
        handleFormSubmit(event)
      }}
    >
      <fieldset
        disabled={submitting}
        style={{ margin: 0, padding: 0, border: 0, minWidth: 0 }}
      >
        <Stack>
          <InfiniteUserSelect
            required
            label={t("Assign")}
            value={form.getValues().userId?.toString()}
            key={form.key("userId")}
            onSelectionChange={(v) =>
              v && form.setFieldValue("userId", parseInt(v))
            }
            filterOptions={{ userTypeFilter: "UsersAndGroups" }}
          />
          <Group justify="space-between">
            <DateTimePicker
              key={`reminder-time-${reminder?.reminderId ?? "new"}-${dateTimePickerResetKey}`}
              flex={1}
              disabled={withoutTime}
              dropdownType="modal"
              modalProps={{ centered: true }}
              valueFormat="L LT"
              presets={presets}
              timePickerProps={{
                withDropdown: true,
                minutesStep: 5,
              }}
              label={t("DueDate")}
              value={form.getValues().time}
              onChange={(v) => {
                debugLog("Selected date:", v)
                const nextValue = v ? dayjs(v).toISOString() : ""
                form.setFieldValue("time", nextValue)

                if (v && presetTimestamps.has(dayjs(nextValue).valueOf())) {
                  setDateTimePickerResetKey((current) => current + 1)
                }
              }}
            />
            <Switch
              label={t("WithoutDueDate")}
              checked={withoutTime}
              onChange={(event) => setWithoutTime(event.currentTarget.checked)}
            />
          </Group>
          <OwnerGroupSingleSelectForReminders
            label={t("Owner")}
            required
            value={form.getValues().ownerGroupId}
            onChange={(v) => v && form.setFieldValue("ownerGroupId", v)}
            key={form.key("ownerGroupId")}
          />
          <Textarea
            required
            label={t("Content")}
            autosize
            minRows={2}
            maxRows={10}
            key={form.key("text")}
            {...form.getInputProps("text")}
          />
          <Switch
            label={t("Completed")}
            key={form.key("completed")}
            {...form.getInputProps("completed", { type: "checkbox" })}
          />
          <Group mt="md" justify="space-between">
            <Tooltip
              label={expanded ? t("HideDetails") : t("ShowDetails")}
              withArrow
            >
              <ActionIcon
                onClick={() => setExpanded(!expanded)}
                variant="subtle"
                size="lg"
                aria-label={expanded ? t("HideDetails") : t("ShowDetails")}
              >
                {expanded ? (
                  <IconChevronCompactUp stroke={2} />
                ) : (
                  <IconChevronCompactDown stroke={2} />
                )}
              </ActionIcon>
            </Tooltip>
            <FormButtonGroup>
              <CancelButton
                variant="default"
                onClick={onCancel}
                disabled={submitting || isDeleting}
              />
              <SaveButton
                loading={submitting}
                disabled={submitting || isDeleting}
              />
              {reminder?.reminderId && onDelete && (
                <button
                  type="button"
                  style={{ marginLeft: 8 }}
                  onClick={onDelete}
                  disabled={submitting || isDeleting}
                  className="mantine-Button-root mantine-Button-color-red"
                >
                  {t("Delete")}
                </button>
              )}
            </FormButtonGroup>
          </Group>
          {expanded && reminder && (
            <Group style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
              <Text>
                {reminder.createdBy && (
                  <>
                    {t("CreatedBy")}
                    <PersonNameWithUserNameFallback
                      userId={reminder.createdBy}
                    />{" "}
                  </>
                )}
                {t("OnThe")}{" "}
                {reminder.createdAt
                  ? getFormattedDate(reminder.createdAt)
                  : reminder.createdAt}
              </Text>
              {reminder.createdAt !== reminder.lastModifiedAt && (
                <Text>
                  {reminder.lastModifiedBy && (
                    <>
                      {t("LastEditedBy")}
                      <PersonNameWithUserNameFallback
                        userId={reminder.lastModifiedBy}
                      />
                    </>
                  )}{" "}
                  {t("OnThe")}{" "}
                  {reminder.lastModifiedAt
                    ? getFormattedDate(reminder.lastModifiedAt)
                    : reminder.lastModifiedAt}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      </fieldset>
    </form>
  )
}
